import React, { useState, useEffect } from 'react';
import { Teacher } from '../../types';
import { X, Plus, Trash2, Edit, AlertCircle, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { Subject, Class } from '../../utils/subjectsClasses';
import SubjectModal from '../ui/SubjectModal';
import ClassModal from '../ui/ClassModal';

interface TeacherFormProps {
    teacher?: Teacher;
    onSubmit: (teacher: Omit<Teacher, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const TeacherForm: React.FC<TeacherFormProps> = ({ teacher, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingTeacherId, setCheckingTeacherId] = useState(false);
    const [teacherIdError, setTeacherIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const { showError } = useToast();

    const [formData, setFormData] = useState({
        teacherId: teacher?.teacherId || '',
        password: '',
        confirmPassword: '',
        firstName: teacher?.firstName || '',
        lastName: teacher?.lastName || '',
        email: teacher?.email || '',
        phone: teacher?.phone || '',
        dateOfBirth: teacher?.dateOfBirth ? teacher.dateOfBirth.toISOString().split('T')[0] : '',
        gender: teacher?.gender || 'male',
        address: {
            street: teacher?.address.street || '',
            city: teacher?.address.city || '',
            state: teacher?.address.state || '',
            zipCode: teacher?.address.zipCode || '',
            country: teacher?.address.country || '',
        },
        qualification: {
            degree: teacher?.qualification.degree || '',
            institution: teacher?.qualification.institution || '',
            yearOfPassing: teacher?.qualification.yearOfPassing || new Date().getFullYear(),
            subjects: teacher?.qualification.subjects || [],
        },
        experience: {
            totalYears: teacher?.experience.totalYears || 0,
            previousSchools: teacher?.experience.previousSchools || [],
        },
        currentPosition: {
            department: teacher?.currentPosition.department || '',
            designation: teacher?.currentPosition.designation || '',
            joiningDate: teacher?.currentPosition.joiningDate ? teacher.currentPosition.joiningDate.toISOString().split('T')[0] : '',
            salary: teacher?.currentPosition.salary || 0,
            status: teacher?.currentPosition.status || 'active',
            inactiveReason: teacher?.currentPosition.inactiveReason || '',
        },
        subjects: teacher?.subjects || [],
        classes: teacher?.classes || [],
        periods: teacher?.periods || [],
        emergencyContact: {
            name: teacher?.emergencyContact.name || '',
            relationship: teacher?.emergencyContact.relationship || '',
            phone: teacher?.emergencyContact.phone || '',
            email: teacher?.emergencyContact.email || '',
        },
        bankDetails: {
            accountNumber: teacher?.bankDetails.accountNumber || '',
            bankName: teacher?.bankDetails.bankName || '',
            ifscCode: teacher?.bankDetails.ifscCode || '',
            accountHolderName: teacher?.bankDetails.accountHolderName || '',
        },
    });

    const [newSubject, setNewSubject] = useState('');
    const [newClass, setNewClass] = useState('');
    const [newPreviousSchool, setNewPreviousSchool] = useState({
        schoolName: '',
        position: '',
        duration: '',
    });
    const [showSubjectModal, setShowSubjectModal] = useState(false);
    const [showClassModal, setShowClassModal] = useState(false);
    const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>([]);
    const [selectedClasses, setSelectedClasses] = useState<Class[]>([]);
    const [teacherIdValid, setTeacherIdValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);

    // Auto-validate Teacher ID when user types (with debounce)
    useEffect(() => {
        if (!teacher && formData.teacherId.trim()) {
            const timer = setTimeout(() => {
                checkTeacherIdUnique(formData.teacherId);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.teacherId]);

    // Auto-validate Email when user types (with debounce)
    useEffect(() => {
        if (!teacher && formData.email.trim()) {
            const timer = setTimeout(() => {
                checkEmailUnique(formData.email);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.email]);

    // Check if teacher ID already exists
    const checkTeacherIdUnique = async (teacherId: string) => {
        if (!teacherId.trim()) {
            setTeacherIdError('');
            return true;
        }

        // Skip check if editing and ID hasn't changed
        if (teacher && teacher.teacherId === teacherId) {
            setTeacherIdError('');
            return true;
        }

        try {
            setCheckingTeacherId(true);
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, where('teacherId', '==', teacherId.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setTeacherIdError('This Teacher ID already exists. Please use a unique ID.');
                setTeacherIdValid(false);
                return false;
            } else {
                setTeacherIdError('');
                setTeacherIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking teacher ID:', error);
            setTeacherIdError('Error validating Teacher ID. Please try again.');
            setTeacherIdValid(false);
            return false;
        } finally {
            setCheckingTeacherId(false);
        }
    };

    // Check if email already exists
    const checkEmailUnique = async (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return true;
        }

        // Skip check if editing and email hasn't changed
        if (teacher && teacher.email === email) {
            setEmailError('');
            return true;
        }

        try {
            setCheckingEmail(true);
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, where('email', '==', email.trim().toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setEmailError('This email is already registered. Please use a different email.');
                setEmailValid(false);
                return false;
            } else {
                setEmailError('');
                setEmailValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking email:', error);
            setEmailError('Error validating email. Please try again.');
            setEmailValid(false);
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Clear errors and valid states when user types
        if (name === 'teacherId') {
            setTeacherIdError('');
            setTeacherIdValid(false);
        }
        if (name === 'email') {
            setEmailError('');
            setEmailValid(false);
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const addSubject = () => {
        if (newSubject.trim()) {
            setFormData(prev => ({
                ...prev,
                subjects: [...prev.subjects, newSubject.trim()]
            }));
            setNewSubject('');
        }
    };

    const removeSubject = (index: number) => {
        setFormData(prev => ({
            ...prev,
            subjects: prev.subjects.filter((_, i) => i !== index)
        }));
    };

    const addClass = () => {
        if (newClass.trim()) {
            setFormData(prev => ({
                ...prev,
                classes: [...prev.classes, newClass.trim()]
            }));
            setNewClass('');
        }
    };

    const removeClass = (index: number) => {
        setFormData(prev => ({
            ...prev,
            classes: prev.classes.filter((_, i) => i !== index)
        }));
    };

    const addPreviousSchool = () => {
        if (newPreviousSchool.schoolName.trim() && newPreviousSchool.position.trim() && newPreviousSchool.duration.trim()) {
            setFormData(prev => ({
                ...prev,
                experience: {
                    ...prev.experience,
                    previousSchools: [...prev.experience.previousSchools, { ...newPreviousSchool }]
                }
            }));
            setNewPreviousSchool({ schoolName: '', position: '', duration: '' });
        }
    };

    const removePreviousSchool = (index: number) => {
        setFormData(prev => ({
            ...prev,
            experience: {
                ...prev.experience,
                previousSchools: prev.experience.previousSchools.filter((_, i) => i !== index)
            }
        }));
    };

    const handleSubjectSelect = (subjects: Subject[]) => {
        setSelectedSubjects(subjects);
        setFormData(prev => ({
            ...prev,
            subjects: subjects.map(s => s.name)
        }));
    };

    const handleClassSelect = (classes: Class[]) => {
        setSelectedClasses(classes);
        setFormData(prev => ({
            ...prev,
            classes: classes.map(c => `${c.grade} - ${c.section}`)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Teacher ID validation
        if (!formData.teacherId.trim()) {
            showError('Teacher ID Required', 'Please enter a Teacher ID.');
            return;
        }

        // Check if Teacher ID is unique
        const isTeacherIdUnique = await checkTeacherIdUnique(formData.teacherId);
        if (!isTeacherIdUnique) {
            showError('Duplicate Teacher ID', 'This Teacher ID already exists. Please use a unique ID.');
            return;
        }

        // Email validation
        if (!formData.email.trim()) {
            showError('Email Required', 'Please enter an email address.');
            return;
        }

        // Check if Email is unique
        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            showError('Duplicate Email', 'This email is already registered. Please use a different email.');
            return;
        }

        // Password validation for new teachers
        if (!teacher) {
            if (!formData.password || formData.password.length < 6) {
                showError('Password Too Short', 'Password must be at least 6 characters long.');
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                showError('Passwords Mismatch', 'Passwords do not match. Please check and try again.');
                return;
            }
        }

        const teacherData = {
            ...formData,
            dateOfBirth: new Date(formData.dateOfBirth),
            currentPosition: {
                ...formData.currentPosition,
                joiningDate: new Date(formData.currentPosition.joiningDate),
                salary: Number(formData.currentPosition.salary),
            },
            experience: {
                ...formData.experience,
                totalYears: Number(formData.experience.totalYears),
            },
            qualification: {
                ...formData.qualification,
                yearOfPassing: Number(formData.qualification.yearOfPassing),
            },
            periods: [], // Initialize empty periods array for new teachers
        };

        onSubmit(teacherData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
                {/* Loading Overlay */}
                {loading && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                            <div className="text-gray-700 font-medium">
                                {teacher ? 'Updating teacher...' : 'Creating teacher...'}
                            </div>
                        </div>
                    </div>
                )}

                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {teacher ? 'Edit Teacher' : 'Add New Teacher'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={`p-6 space-y-8 relative ${loading ? 'pointer-events-none opacity-50' : ''}`}>
                    {/* Basic Information */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Teacher ID <span className="text-red-500">*</span>
                                    {teacher && (
                                        <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                    )}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="teacherId"
                                        value={formData.teacherId}
                                        onChange={handleInputChange}
                                        placeholder="Enter unique teacher ID"
                                        required
                                        readOnly={!!teacher}
                                        disabled={checkingTeacherId}
                                        className={`input-field ${teacher
                                            ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                            : teacherIdError
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : ''
                                            }`}
                                    />
                                    {!teacher && checkingTeacherId && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                        </div>
                                    )}
                                    {!teacher && !checkingTeacherId && teacherIdError && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <AlertCircle className="h-4 w-4 text-red-500" />
                                        </div>
                                    )}
                                    {!teacher && !checkingTeacherId && !teacherIdError && teacherIdValid && formData.teacherId.trim() && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        </div>
                                    )}
                                </div>
                                {!teacher && teacherIdError && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-3 w-3 mr-1" />
                                        {teacherIdError}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email <span className="text-red-500">*</span>
                                    {teacher && (
                                        <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                    )}
                                </label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        required
                                        readOnly={!!teacher}
                                        disabled={checkingEmail}
                                        className={`input-field ${teacher
                                            ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                            : emailError
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : ''
                                            }`}
                                    />
                                    {!teacher && checkingEmail && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                        </div>
                                    )}
                                    {!teacher && !checkingEmail && emailError && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <AlertCircle className="h-4 w-4 text-red-500" />
                                        </div>
                                    )}
                                    {!teacher && !checkingEmail && !emailError && emailValid && formData.email.trim() && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        </div>
                                    )}
                                </div>
                                {!teacher && emailError && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-3 w-3 mr-1" />
                                        {emailError}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date of Birth <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Gender <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                >
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            {!teacher && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                placeholder="Create a secure password"
                                                required={!teacher}
                                                className="input-field pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm Password <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                placeholder="Confirm your password"
                                                required={!teacher}
                                                className="input-field pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Address Information */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Street Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address.street"
                                    value={formData.address.street}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address.city"
                                    value={formData.address.city}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    State <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address.state"
                                    value={formData.address.state}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ZIP Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address.zipCode"
                                    value={formData.address.zipCode}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Country <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address.country"
                                    value={formData.address.country}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Qualification */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Qualification</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Degree <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="qualification.degree"
                                    value={formData.qualification.degree}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Institution <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="qualification.institution"
                                    value={formData.qualification.institution}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Year of Passing <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="qualification.yearOfPassing"
                                    value={formData.qualification.yearOfPassing}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Experience */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Experience</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Total Years of Experience <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="experience.totalYears"
                                    value={formData.experience.totalYears}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    min="0"
                                    required
                                />
                            </div>
                        </div>

                        {/* Previous Schools */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Previous Schools</label>
                            <div className="space-y-3">
                                {formData.experience.previousSchools.map((school, index) => (
                                    <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-1">
                                            <p className="font-medium">{school.schoolName}</p>
                                            <p className="text-sm text-gray-600">{school.position} - {school.duration}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removePreviousSchool(index)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    <input
                                        type="text"
                                        placeholder="School Name"
                                        value={newPreviousSchool.schoolName}
                                        onChange={(e) => setNewPreviousSchool(prev => ({ ...prev, schoolName: e.target.value }))}
                                        className="input-field"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Position"
                                        value={newPreviousSchool.position}
                                        onChange={(e) => setNewPreviousSchool(prev => ({ ...prev, position: e.target.value }))}
                                        className="input-field"
                                    />
                                    <div className="flex space-x-2">
                                        <input
                                            type="text"
                                            placeholder="Duration"
                                            value={newPreviousSchool.duration}
                                            onChange={(e) => setNewPreviousSchool(prev => ({ ...prev, duration: e.target.value }))}
                                            className="input-field"
                                        />
                                        <button
                                            type="button"
                                            onClick={addPreviousSchool}
                                            className="btn-primary"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Current Position */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Current Position</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Department <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="currentPosition.department"
                                    value={formData.currentPosition.department}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Designation <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="currentPosition.designation"
                                    value={formData.currentPosition.designation}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Joining Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="currentPosition.joiningDate"
                                    value={formData.currentPosition.joiningDate}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Salary <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="currentPosition.salary"
                                    value={formData.currentPosition.salary}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    min="0"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="currentPosition.status"
                                    value={formData.currentPosition.status}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    required
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="resigned">Resigned</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Subjects and Classes */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Subjects & Classes</h3>

                        {/* Subjects */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Subjects</label>
                            <button
                                type="button"
                                onClick={() => {
                                    if (selectedClasses.length === 0) {
                                        showError('No Classes Selected', 'Please select classes first to choose subjects');
                                        return;
                                    }
                                    setShowSubjectModal(true);
                                }}
                                className={`w-full p-3 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center space-x-2 ${selectedClasses.length === 0
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                    : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50'
                                    }`}
                                disabled={selectedClasses.length === 0}
                            >
                                <Edit className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-600">
                                    {selectedClasses.length === 0
                                        ? 'Select classes first to choose subjects'
                                        : selectedSubjects.length > 0
                                            ? `${selectedSubjects.length} subject${selectedSubjects.length !== 1 ? 's' : ''} selected`
                                            : 'Click to select subjects'
                                    }
                                </span>
                            </button>
                            {selectedSubjects.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {selectedSubjects.map((subject) => (
                                        <span
                                            key={subject.id}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                        >
                                            {subject.name}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = selectedSubjects.filter(s => s.id !== subject.id);
                                                    handleSubjectSelect(updated);
                                                }}
                                                className="ml-2 hover:text-blue-600"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Classes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Classes</label>
                            <button
                                type="button"
                                onClick={() => setShowClassModal(true)}
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center justify-center space-x-2"
                            >
                                <Edit className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-600">
                                    {selectedClasses.length > 0
                                        ? `${selectedClasses.length} class${selectedClasses.length !== 1 ? 'es' : ''} selected`
                                        : 'Click to select classes'
                                    }
                                </span>
                            </button>
                            {selectedClasses.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {selectedClasses.map((cls) => (
                                        <span
                                            key={cls.id}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                        >
                                            {cls.grade} - {cls.section}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = selectedClasses.filter(c => c.id !== cls.id);
                                                    handleClassSelect(updated);
                                                }}
                                                className="ml-2 hover:text-green-600"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Emergency Contact */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Emergency Contact</h3>
                        <p className="text-sm text-gray-500 mb-4">Optional - Can be added later</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                <input
                                    type="text"
                                    name="emergencyContact.name"
                                    value={formData.emergencyContact.name}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                                <input
                                    type="text"
                                    name="emergencyContact.relationship"
                                    value={formData.emergencyContact.relationship}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                <input
                                    type="tel"
                                    name="emergencyContact.phone"
                                    value={formData.emergencyContact.phone}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    name="emergencyContact.email"
                                    value={formData.emergencyContact.email}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Bank Details</h3>
                        <p className="text-sm text-gray-500 mb-4">Optional - Can be added later</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                                <input
                                    type="text"
                                    name="bankDetails.accountNumber"
                                    value={formData.bankDetails.accountNumber}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                                <input
                                    type="text"
                                    name="bankDetails.bankName"
                                    value={formData.bankDetails.bankName}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                                <input
                                    type="text"
                                    name="bankDetails.ifscCode"
                                    value={formData.bankDetails.ifscCode}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                                <input
                                    type="text"
                                    name="bankDetails.accountHolderName"
                                    value={formData.bankDetails.accountHolderName}
                                    onChange={handleInputChange}
                                    className="input-field"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || checkingTeacherId || checkingEmail || !!teacherIdError || !!emailError}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Saving...' : teacher ? 'Update Teacher' : 'Add Teacher'}
                        </button>
                    </div>
                </form>

                {/* Subject Selection Modal */}
                <SubjectModal
                    isOpen={showSubjectModal}
                    onClose={() => setShowSubjectModal(false)}
                    onSelect={handleSubjectSelect}
                    selectedSubjects={selectedSubjects}
                    selectedClasses={selectedClasses}
                    title="Select Subjects"
                />

                {/* Class Selection Modal */}
                <ClassModal
                    isOpen={showClassModal}
                    onClose={() => setShowClassModal(false)}
                    onSelect={handleClassSelect}
                    selectedClasses={selectedClasses}
                    title="Select Classes"
                />
            </div>
        </div>
    );
};

export default TeacherForm;
